<template>
  <c-input
    v-bind="$attrs"
    ref="inputRef"
    v-model="displayValue"
    class="date-input"
    hide-bottom-space
    mask="##-##-####"
    placeholder="DD-MM-JJJJ"
    :error="errorMessage !== undefined"
    :error-message="errorMessage"
    :rules
    @update:model-value="handleTypedInput"
    @focus="openCalendar"
  >
    <template #append>
      <q-icon
        size="xs"
        color="primary"
        class="tw-cursor-pointer"
        :name="toDuotoneIcon(inputDateIcon)"
        @click="openCalendar"
      />
    </template>

    <q-popup-proxy
      ref="datePopupProxyRef"
      no-parent-event
      no-focus
      no-refocus
      transition-show="jump-down"
      transition-hide="jump-up"
      @show="syncCalendarView"
      @hide="handlePopupHidden"
    >
      <q-date
        ref="calendarRef"
        v-model="model"
        v-bind="$attrs"
        mask="DD-MM-YYYY"
        :options="isDateAllowed"
        minimal
        @update:model-value="closeCalendar"
      >
        <div class="row items-center justify-end">
          <c-button-text ref="closeButtonRef" v-close-popup label="Sluiten" />
        </div>
      </q-date>
    </q-popup-proxy>
  </c-input>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeMount,
  ref,
  useAttrs,
  useTemplateRef,
  watch,
} from "vue";
import {
  toDutchDateString,
  getDateStringFromQuasar,
} from "shared/utils/dateHelpers";
import { inputDateIcon, toDuotoneIcon } from "common/defaultIcons";
import { date, type QBtn, type QDate, type QPopupProxy } from "quasar";

// Props
const props = withDefaults(
  defineProps<{
    minDate?: string | null;
    maxDate?: string;
    changeToMinDate?: boolean;
    required?: boolean;
  }>(),
  {
    minDate: null,
    changeToMinDate: false,
    required: true,
  },
);

// Emits
const emit = defineEmits<{
  (event: "close", value: { value: string | null; hasChanged: boolean }): void;
}>();

const attrs = useAttrs();

// Model
const model = defineModel<string | null>({
  required: true,
  default: () => "",
});

// Template refs
const inputRef = useTemplateRef("inputRef");
const calendarRef = useTemplateRef<InstanceType<typeof QDate>>("calendarRef");
const datePopupProxyRef =
  useTemplateRef<InstanceType<typeof QPopupProxy>>("datePopupProxyRef");
const closeButtonRef =
  useTemplateRef<InstanceType<typeof QBtn>>("closeButtonRef");

// State
const initialValue = ref<string | null>(null);
const errorMessage = ref<string | undefined>(undefined);
const displayValue = ref(model.value ?? "");

// Sync model -> displayValue when model changes (e.g. from calendar pick)
watch(model, (newVal) => {
  displayValue.value = newVal ?? "";
});

// --- Input handling ---

const handleTypedInput = (value: string | number | null) => {
  const str = String(value ?? "");

  if (str === "") {
    model.value = null;
    return;
  }

  // Only sync when the full date is typed (10 chars: DD-MM-YYYY)
  if (str.length !== 10) {
    return;
  }

  const [day, month, year] = str.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  const isValidParsedDate =
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day;

  if (isValidParsedDate) {
    model.value = str;
  }
};

// --- Calendar popup ---

const openCalendar = () => {
  // A readonly/disabled field still receives focus, so guard the picker here or
  // it would let the value be changed anyway.
  if (attrs.readonly || attrs.disable) {
    return;
  }

  datePopupProxyRef.value?.show();
};

const closeCalendar = () => {
  // Quasar requires triggering a click on v-close-popup to close the proxy
  closeButtonRef.value?.$el.click();
};

const syncCalendarView = () => {
  if (!calendarRef.value) {
    return;
  }

  const dateStr = props.minDate || model.value;

  const dateView =
    dateStr != null && dateStr !== ""
      ? date.extractDate(
          dateStr,
          dateStr.includes("/") ? "YYYY/MM/DD" : "DD-MM-YYYY",
        )
      : new Date();

  calendarRef.value.setCalendarTo(
    dateView.getFullYear(),
    dateView.getMonth() + 1,
  );
};

const handlePopupHidden = () => {
  emit("close", { value: model.value, hasChanged: hasChanged.value });
  initialValue.value = model.value;
};

const hasChanged = computed(() => initialValue.value !== model.value);

// --- Validation ---

const isDateAllowed = (dateStr: string) => {
  const minDate = props.minDate && getDateStringFromQuasar(props.minDate);
  const maxDate = props.maxDate && getDateStringFromQuasar(props.maxDate);

  return (!minDate || dateStr >= minDate) && (!maxDate || dateStr <= maxDate);
};

const validateDateRange = (val: string | null): boolean => {
  const currentDateStr = getDateStringFromQuasar(val);

  if (currentDateStr === undefined) {
    errorMessage.value = undefined;
    return true;
  }

  const minDateStr = getDateStringFromQuasar(props.minDate);
  const maxDateStr = getDateStringFromQuasar(props.maxDate);
  const currentDate = new Date(currentDateStr);
  const minDate = minDateStr ? new Date(minDateStr) : null;
  const maxDate = maxDateStr ? new Date(maxDateStr) : null;

  if (minDate && currentDate < minDate) {
    if (props.changeToMinDate) {
      model.value = toDutchDateString(minDate);
    }
    errorMessage.value = "Deze datum is niet geldig";
    return false;
  }

  if (maxDate && currentDate > maxDate) {
    if (props.changeToMinDate) {
      model.value = toDutchDateString(maxDate);
    }
    errorMessage.value = "Deze datum is niet geldig";
    return false;
  }

  errorMessage.value = undefined;
  return true;
};

const rules = computed(() => [
  ...(props.required
    ? [(val: string | null) => !!val || "Dit veld is verplicht"]
    : []),
  (val: string | null) => validateDateRange(val) || "Deze datum is niet geldig",
]);

// Re-validate when min/max/model changes
watch(
  [() => props.minDate, () => props.maxDate, () => model.value],
  ([minDate, , modelVal]) => {
    if (minDate === "") {
      return;
    }
    validateDateRange(modelVal);
  },
  { immediate: true },
);

// --- Lifecycle ---

onBeforeMount(() => {
  initialValue.value = model.value;
});

// --- Expose ---

const isInputValid = async () => {
  return await inputRef.value?.inputRef?.validate();
};

defineExpose({ focus: openCalendar, isInputValid });
</script>

<style lang="scss">
label.q-field.date-input {
  @apply tw-cursor-pointer;

  &.q-field--outlined {
    .q-field__control::before {
      @apply tw-transition;
    }

    &:hover {
      .q-field__control::before {
        @apply tw-border-primary;
      }
    }
  }

  .q-field__append {
    @apply tw-cursor-pointer;
  }
}
</style>
